import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {openErrorModal} from '@Modals/error-modal';
import type {Dimensions} from '@floating-ui/react';
import {isIOSAppOrEmbeddedEditor} from '@Utils/browser.util';
import {deleteCookie, readCookie} from '@Utils/cookie.util';
import {getStateFromUrl} from '@Utils/url.util';
import type * as GooglePicker from '../types/google-picker';

const OAUTH_DATA_COOKIE = 'pmwsac';

/**
 * Max height of Google picker are mentioned at https://developers.google.com/picker/docs/reference
 */
const PICKER_MAX_HEIGHT = 650;
/**
 * Max width of Google picker are mentioned at https://developers.google.com/picker/docs/reference
 */
const PICKER_MAX_WIDTH = 1051;
const PICKER_PADDING = 40;
const LOGIN_WINDOW_WIDTH = 500;
const LOGIN_WINDOW_HEIGHT = 570;
let init = false;
let googlePicker: GooglePicker.picker.Picker;
let pickerApiLoaded = false;
let oauthToken: string;
let apiKey: string;
let oauthUrl: string;
let onFolderSelectedCallback: ((folderId: string) => Promise<void>) | undefined;
let lastSelectedFolderId: string | null = null;

// Opens the Google Drive picker to select a destination folder only (no downloadUrl yet).
// Use saveToGoogleDriveWithSelectedFolder later once you have the downloadUrl ready.
export const openGoogleDriveFolderPicker = async (
  url: string,
  onFolderSelected?: (folderId: string) => Promise<void>
): Promise<void> => {
  oauthUrl = url;
  onFolderSelectedCallback = onFolderSelected;
  if (init) {
    if (arePickerDimensionsValid(googlePicker)) {
      setPickerVisible(googlePicker);
    } else {
      createPicker();
    }
  } else {
    await initialize();
  }
};

const initialize = async (): Promise<void> => {
  try {
    await initGoogleLogin();
    showLoading('loadGoogleDrive', {
      text: window.i18next.t('pmwjs_loading_google_drive'),
      hideIcon: true,
    });
    window.PMW.google.loadFeaturesAPI(() => onLoggedIn());
  } catch (e) {
    hideLoading('loadGoogleDrive');
    openErrorModal();
  }
};

const calculatePickerDimensions = (): Dimensions => {
  return {
    width: window.innerWidth > PICKER_MAX_WIDTH ? PICKER_MAX_WIDTH : window.innerWidth - PICKER_PADDING,
    height: window.innerHeight > PICKER_MAX_HEIGHT ? PICKER_MAX_HEIGHT : window.innerHeight - PICKER_PADDING,
  };
};

const arePickerDimensionsValid = (picker: GooglePicker.picker.Picker | undefined): boolean => {
  if (picker) {
    const dimensions = calculatePickerDimensions();
    return !(Math.abs(dimensions.width - picker.jha) > 10 || Math.abs(dimensions.height - picker.iha) > 10);
  }
  return false;
};

const onLoggedIn = (): void => {
  createPicker();

  if (typeof window.gapi !== 'undefined') {
    window.gapi.load('picker', {callback: onPickerApiLoad});
  } else {
    window.PMW.google.onError();
  }
};

const onPickerApiLoad = (): void => {
  pickerApiLoaded = true;
  if (!googlePicker && oauthToken) {
    createPicker();
  }
};

const createPicker = (): void => {
  if (pickerApiLoaded && oauthToken) {
    const pickerDimensions = calculatePickerDimensions();
    const language = window.PMW.util.getLanguage();
    
    // Use DocsView with ViewId.FOLDERS to show only folders
    const viewFolders = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
      .setIncludeFolders(true)
      .setOwnedByMe(true)
      .setSelectFolderEnabled(true);

    googlePicker = new window.google.picker.PickerBuilder()
      .addView(viewFolders)
      .enableFeature(window.google.picker.Feature.NAV_HIDDEN)
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setLocale(language)
      .setSize(pickerDimensions.width, pickerDimensions.height)
      .setTitle(window.i18next.t('pmwjs_select_folder'))
      .setOAuthToken(oauthToken)
      .setDeveloperKey(apiKey)
      .setCallback((data: GooglePicker.picker.ResponseObject) => {
        void driveDownloadPickerCallback(data);
      })
      .build();

    setPickerVisible(googlePicker);
    hideLoading('loadGoogleDrive');
  }
};

const setPickerVisible = (picker: GooglePicker.picker.Picker | undefined): void => {
  if (picker) {
    picker.setVisible(true);
  }
};

const driveDownloadPickerCallback = async (data: GooglePicker.picker.ResponseObject): Promise<void> => {
  if (data.action === window.google.picker.Action.PICKED) {
    const docs = data[window.google.picker.Response.DOCUMENTS];
    const folderId = docs[0].id;


    lastSelectedFolderId = folderId;
    if (onFolderSelectedCallback) {
      await onFolderSelectedCallback(folderId);
    }
  }
};

// Call this after selecting a folder (via openGoogleDriveFolderPicker) once the download URL is ready
export const saveToGoogleDriveWithSelectedFolder = async (downloadUrl: string, fileName: string): Promise<void> => {
  if (!lastSelectedFolderId) {
    openErrorModal({
      message: window.i18next.t('pmwjs_error_saving_to_google_drive'),
    });
    return;
  }

  showLoading('saveToGoogleDrive', {
    text: window.i18next.t('pmwjs_saving_to_google_drive'),
    hideIcon: true,
  });

  try {
    await window.PMW.writeLocal('cart/saveToGoogleDrive', {
      downloadUrl,
      fileName,
      folderId: lastSelectedFolderId,
      accessToken: JSON.stringify({access_token: oauthToken}),
    });
  } catch (e) {
    openErrorModal({
      message: window.i18next.t('pmwjs_error_saving_to_google_drive'),
    });
  } finally {
    hideLoading('saveToGoogleDrive');
  }
};

const initGoogleLogin = async (): Promise<void> => {
  const loginWindow = window.open(oauthUrl, '_blank', `width=${LOGIN_WINDOW_WIDTH},height=${LOGIN_WINDOW_HEIGHT}`);
  if (isIOSAppOrEmbeddedEditor()) {
    await checkLoggedInStatus();
    return;
  }
  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      const cookie = readCookie(OAUTH_DATA_COOKIE);
      if (cookie) {
        clearInterval(interval);
        deleteCookie(OAUTH_DATA_COOKIE);
        loginWindow?.close();
        const response = JSON.parse(decodeURIComponent(cookie));
        if (response.status === 'success') {
          oauthToken = response.data.token;
          apiKey = response.data.apiKey;
          init = true;
          resolve();
        } else {
          reject();
        }
      }
    }, 500);
  });
};

const checkLoggedInStatus = async (): Promise<void> => {
  const response = await window.PMW.pollingAjaxCallAsync(
    async () => {},
    () => {
      return window.PMW.readLocal('authenticate/hasAccountBeenLoggedIn', {
        state: getStateFromUrl(oauthUrl ?? ''),
      });
    }
  );
  if (response.status === 'success') {
    oauthToken = response.data.token;
    apiKey = response.data.apiKey;
    init = true;
  }
};
